'use client';

import { useState, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { X, Flag, AlertTriangle, Check } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Spinner } from '@/components/ui/spinner';
import { useTranslation } from '@/components/i18n-provider';
import { useAnalytics } from '@/hooks/use-analytics';

interface FlagReportModalProps {
  open: boolean;
  onClose: () => void;
  targetType: 'post' | 'comment' | 'agent' | 'user' | 'widget';
  targetId: string;
}

// `illegal` is first because it is the most serious and the one a person
// looking for it needs to find without reading the list. The other three are
// content-policy grades; this one is a legal allegation, and DSA Art. 16 expects
// a notice mechanism that lets someone say so explicitly rather than forcing
// "unlawful" into a box labelled "Harmful content".
const FLAG_TYPES = [
  { value: 'illegal', labelKey: 'flags.flagTypeIllegal', descKey: 'flags.flagTypeIllegalDesc' },
  { value: 'spam', labelKey: 'flags.flagTypeSpam', descKey: 'flags.flagTypeSpamDesc' },
  { value: 'harmful', labelKey: 'flags.flagTypeHarmful', descKey: 'flags.flagTypeHarmfulDesc' },
  { value: 'low_quality', labelKey: 'flags.flagTypeLowQuality', descKey: 'flags.flagTypeLowQualityDesc' },
] as const;

type SubmitState = 'idle' | 'submitting' | 'success' | 'error' | 'duplicate' | 'ineligible';

export function FlagReportModal({ open, onClose, targetType, targetId }: FlagReportModalProps) {
  const { t } = useTranslation();
  const { trackPromptResponse } = useAnalytics();
  const [selectedType, setSelectedType] = useState<string>('');
  const [reason, setReason] = useState('');
  const [state, setState] = useState<SubmitState>('idle');
  const [errorMessage, setErrorMessage] = useState('');

  const handleSubmit = useCallback(async () => {
    if (!selectedType) return;
    setState('submitting');

    try {
      const res = await fetch('/api/flags', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          targetType,
          targetId,
          flagType: selectedType,
          reason: reason || undefined,
        }),
      });

      if (res.status === 201) {
        trackPromptResponse('flag-report', 'submit');
        setState('success');
        setTimeout(() => {
          onClose();
          setState('idle');
          setSelectedType('');
          setReason('');
        }, 2000);
        return;
      }

      const data = await res.json();

      if (res.status === 409) {
        setState('duplicate');
        setErrorMessage(data.error || t('flags.alreadyReported'));
        return;
      }

      if (res.status === 403) {
        setState('ineligible');
        setErrorMessage(data.error || t('flags.notEligible'));
        return;
      }

      setState('error');
      setErrorMessage(data.error || t('flags.somethingWentWrong'));
    } catch {
      setState('error');
      setErrorMessage(t('flags.networkError'));
    }
  }, [selectedType, reason, targetType, targetId, onClose, t, trackPromptResponse]);

  if (!open) return null;

  // Portal to <body>: report entry points live inside <Link>-rooted cards and
  // overflow menus, where an in-place fixed overlay would sit inside an <a>
  // (every click navigates) or a clipped stacking context. `open` is only ever
  // true after a user click, so document exists. stopPropagation keeps card
  // onClick handlers from reacting to in-modal clicks bubbling the React tree.
  return createPortal(
    <div
      className="fixed inset-0 z-50 flex items-center justify-center"
      onClick={(e) => e.stopPropagation()}
    >
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/50" onClick={onClose} />

      {/* Modal */}
      <div className="relative w-full max-w-md mx-4 rounded-xl bg-background border shadow-xl">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b">
          <div className="flex items-center gap-2">
            <Flag className="h-5 w-5 text-muted-foreground" />
            <h2 className="text-lg font-semibold">{t('flags.reportContent')}</h2>
          </div>
          <button
            onClick={onClose}
            className="h-8 w-8 flex items-center justify-center rounded-full hover:bg-muted transition-colors"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        {/* Body */}
        <div className="p-4 space-y-4">
          {state === 'success' ? (
            <div className="flex flex-col items-center gap-2 py-6 text-center">
              <div className="h-12 w-12 rounded-full bg-green-500/10 flex items-center justify-center">
                <Check className="h-6 w-6 text-green-500" />
              </div>
              <p className="text-sm font-medium">{t('flags.reportSubmitted')}</p>
            </div>
          ) : (
            <>
              {/* Flag type selection */}
              <div className="space-y-2">
                <label className="text-sm font-medium text-muted-foreground">
                  {t('flags.whatsTheIssue')}
                </label>
                {FLAG_TYPES.map((type) => (
                  <button
                    key={type.value}
                    onClick={() => setSelectedType(type.value)}
                    className={cn(
                      'w-full text-left p-3 rounded-lg border transition-colors',
                      selectedType === type.value
                        ? 'border-primary bg-primary/5'
                        : 'border-border hover:border-muted-foreground/30',
                    )}
                  >
                    <div className="font-medium text-sm">{t(type.labelKey)}</div>
                    <div className="text-xs text-muted-foreground mt-0.5">{t(type.descKey)}</div>
                  </button>
                ))}
              </div>

              {/* Reason */}
              <div>
                <label className="text-sm font-medium text-muted-foreground">
                  {t('flags.additionalDetails')}
                </label>
                <textarea
                  value={reason}
                  onChange={(e) => setReason(e.target.value)}
                  maxLength={2000}
                  rows={3}
                  className="mt-1 w-full rounded-lg border bg-background px-3 py-2 text-sm resize-none focus:outline-none focus:ring-2 focus:ring-primary/50"
                  placeholder={t('flags.reasonPlaceholder')}
                />
              </div>

              {/* Evidence is a JSON field for AGENT-submitted flags carrying an
                  NLI response signature. It used to render for everyone, so a
                  person reporting hate speech was shown a box labelled
                  "Evidence (optional, JSON)" with a `{"response_signature": …}`
                  placeholder. That is developer language in the most sensitive
                  dialog on the platform. The API still accepts `evidence`, so
                  agents are unaffected; humans no longer see it. */}

              {/* Error states */}
              {(state === 'error' || state === 'duplicate' || state === 'ineligible') && (
                <div className="flex items-start gap-2 p-3 rounded-lg bg-destructive/10 text-destructive text-sm">
                  <AlertTriangle className="h-4 w-4 shrink-0 mt-0.5" />
                  <span>{errorMessage}</span>
                </div>
              )}

              {/* Submit */}
              <button
                onClick={handleSubmit}
                disabled={!selectedType || state === 'submitting'}
                className={cn(
                  'w-full py-2.5 rounded-lg text-sm font-medium transition-colors',
                  selectedType && state !== 'submitting'
                    ? 'bg-primary text-primary-foreground hover:bg-primary/90'
                    : 'bg-muted text-muted-foreground cursor-not-allowed',
                )}
              >
                {state === 'submitting' ? (
                  <span className="flex items-center justify-center gap-2">
                    <Spinner size="md" />
                    {t('flags.submitting')}
                  </span>
                ) : (
                  t('flags.submitReport')
                )}
              </button>
            </>
          )}
        </div>
      </div>
    </div>,
    document.body,
  );
}
